import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Center,
    Spinner,
    Table,
    Tbody,
    Td,
    Th,
    Thead,
    Tr,
    useToast,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    Text
} from "@chakra-ui/react";
import { getLastOMCSyncLog } from "../../api/getLastOMCSyncLog";
import { manuallySyncOMCEntity } from "../../api/manualOMCEntitySync";
import { FaSync } from "react-icons/fa";
import getPrettyOMCEntity from "../../../../helpers/omcEntity";
import { getCompanies } from "../../api";

const NomenclatureTable = () => {
    const [omcEntity, setOmcEntity] = useState([]);
    const [omcCompanies, setOmcCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [syncingEntity, setSyncingEntity] = useState(null);
    const [loadingSync, setLoadingSync] = useState(false);
    const toast = useToast();

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const omcCompanies = await getCompanies();
            const omcEntities = await getLastOMCSyncLog();

            setOmcCompanies(omcCompanies || []);
            setOmcEntity(omcEntities || []);
        } catch (error) {
            toast({
                title: "Eroare",
                description: "Nu s-au putut încărca datele.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleActionClick = (entity) => {
        if (syncingEntity || loadingSync) return;
        setSyncingEntity(entity);
        setLoadingSync(true);

        toast({
            title: "Acțiune",
            description: `Trimitere către endpoint pentru entitatea ${getPrettyOMCEntity(entity)}`,
            status: "info",
            duration: 3000,
            isClosable: true,
        });

        manuallySyncOMCEntity(entity)
            .then(() => {
                toast({
                    title: "Succes",
                    description: `Sincronizare completă pentru entitatea ${getPrettyOMCEntity(entity)}`,
                    status: "success",
                    duration: 5000,
                    isClosable: true,
                });
                fetchData();
            })
            .catch((error) => {
                console.error(error);
                toast({
                    title: "Eroare",
                    description: `A apărut o problemă la efectuarea acțiunii pentru ${getPrettyOMCEntity(entity)}`,
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            })
            .finally(() => {
                setSyncingEntity(null);
                setLoadingSync(false);
            });
    };

    const groupedEntities = omcEntity.reduce((acc, item) => {
        if (!acc[item.cif]) acc[item.cif] = [];
        acc[item.cif].push(item);
        return acc;
    }, {});

    return (
        <Box overflowX="auto">
            {loading ? (
                <Center height="300px" bg="gray.100">
                    <Spinner size="xl" />
                </Center>
            ) : (
                <>
                    <Box mb={4}>
                        <Table variant="striped" size="sm">
                            <Thead>
                                <Tr>
                                    <Th>Entitate</Th>
                                    <Th textAlign="right">Acțiuni</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {omcEntity.length > 0 ? (
                                    [...new Set(omcEntity.map(item => item.entity))].map((entry, index) => (
                                        <Tr key={index}>
                                            <Td>{getPrettyOMCEntity(entry)}</Td>
                                            <Td textAlign="right">
                                                <Button
                                                    variant="outline"
                                                    bg="white"
                                                    colorScheme="orange"
                                                    _hover={{bg: 'orange.100'}}
                                                    size="sm"
                                                    onClick={() => handleActionClick(entry)}
                                                    isDisabled={loadingSync || syncingEntity}
                                                    isLoading={syncingEntity === entry}
                                                    leftIcon={<FaSync />}
                                                >
                                                    {syncingEntity === entry ? (
                                                        <Spinner size="sm" />
                                                    ) : (
                                                        "Sincronizare"
                                                    )}
                                                </Button>
                                            </Td>
                                        </Tr>
                                    ))
                                ) : (
                                    <Tr>
                                        <Td colSpan={5} textAlign="center">Nu există date disponibile</Td>
                                    </Tr>
                                )}
                            </Tbody>
                        </Table>
                    </Box>
                    <Text pb="3" pt="3" fontWeight="bold" borderBottom="1px solid" borderColor="gray.300">
                        Detalii Nomenclator
                    </Text>
                    <Accordion allowMultiple>
                        {Object.entries(groupedEntities).map(([cif, entities]) => (
                            <AccordionItem key={cif}>
                                <h2>
                                    <AccordionButton _expanded={{ bg: "gray.200" }}>
                                        <Box flex="1" textAlign="left" fontWeight="bold">
                                            {omcCompanies.find(e => e.cif === cif)?.name}
                                        </Box>
                                        <AccordionIcon />
                                    </AccordionButton>
                                </h2>
                                <AccordionPanel p={4}>
                                    <Table variant="striped" size="sm">
                                        <Thead>
                                            <Tr>
                                                <Th>Entitate</Th>
                                                <Th>Data ultimei sincronizări</Th>
                                                <Th>Total entități</Th>
                                                <Th>Entități indexate la ultima actualizare</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {entities.map((entry, index) => (
                                                <Tr key={`${cif}-${index}`}>
                                                    <Td>{getPrettyOMCEntity(entry.entity)}</Td>
                                                    <Td>{entry.timestamp ? new Date(entry.timestamp).toLocaleString() : "N/A"}</Td>
                                                    <Td>{entry.totalEntities}</Td>
                                                    <Td>{entry.indexedEntities}</Td>
                                                </Tr>
                                            ))}
                                        </Tbody>
                                    </Table>
                                </AccordionPanel>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </>
            )}
        </Box>
    );
};

export default NomenclatureTable;
